import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import ProjectHeader from "../_shared/ProjectHeader/ProjectHeader";
import { ArrowRightIcon, ProgressBar } from "./StrategyCanvasHelpers";
import "./StrategyCanvas.scss";
import StrategyCanvasSteps from "./StrategyCanvasSteps";
import StrategyCanvasSkeleton from "./StrategyCanvasSkeleton";
import { HeaderContext } from "../../../HeaderContext";
import { STRATEGY_CANVAS_BRIEF, STRATEGY_CANVAS_VIEW } from "../../../data/RouteUrls";
import useStrategyCanvasStore, {
  useStrategyCanvasStoreSelector,
} from "./_redux/StrategyCanvasStore";

const selectDashboard = (state) => ({
  clients: state.clients,
  dashboardLoading: state.dashboardLoading,
});

const SmeCanvasCard = ({
  briefId,
  initials,
  name,
  ceo,
  filled,
  statusText,
  statusSubText,
  briefDisabled,
  buttonText = "View brief",
  stepDates,
  isOpen,
  onToggle,
}) => {
  const history = useHistory();
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  const goToBrief = (event) => {
    event.stopPropagation();
    if (briefDisabled) return;
    // Once a canvas is LOCKED, the dashboard button label flips to "View
    // Strategy Canvas" (section 3 of the integration doc) - route to the
    // standalone read-only view instead of the brief page in that case.
    const destination =
      buttonText === "View Strategy Canvas" ? STRATEGY_CANVAS_VIEW : STRATEGY_CANVAS_BRIEF;
    history.push(destination.replace(":briefId", briefId));
  };

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    } else {
      setContentHeight(0);
    }
  }, [isOpen, filled]);

  return (
    <div className={`sc-card ${isOpen ? "open" : ""}`}>
      <div
        className="sc-card-row"
        onClick={onToggle}
        role="button"
        tabIndex={0}
      >
        <div className="sc-card-left">
          <div className="sc-avatar">{initials}</div>
          <div className="sc-info">
            <div className="sc-name">{name}</div>
            <div className="sc-ceo">{ceo}</div>
            <div className="sc-progress-row">
              <ProgressBar filled={filled} />
              <div className="sc-status">
                <span className="sc-status-text">{statusText}</span>
                {statusSubText ? (
                  <>
                    <span className="sc-status-divider" />
                    <span className="sc-status-sub">{statusSubText}</span>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className="sc-card-actions">
          <button
            className="sc-brief-btn"
            disabled={briefDisabled}
            onClick={goToBrief}
          >
            {buttonText}
            <ArrowRightIcon />
          </button>
        </div>
      </div>
      <div className="sc-accordion" style={{ maxHeight: contentHeight }}>
        <div ref={contentRef} className="sc-accordion-inner">
          <div className="sc-accordion-divider" />
          <StrategyCanvasSteps filled={filled} stepDates={stepDates} />
        </div>
      </div>
    </div>
  );
};

// Keep in sync with the $accordion-transition-duration used in
// StrategyCanvas.scss for the .sc-accordion max-height transition.
const ACCORDION_TRANSITION_MS = 350;

const StrategyCanvas = () => {
  // Only one row is ever open at a time. To avoid the previously-open card
  // collapsing at the exact same moment the newly clicked card expands
  // (which made the new card look like it was "jumping upward"), we close
  // the current one first and only start opening the new one once that
  // collapse animation has finished.
  const [openId, setOpenId] = useState(null);
  const openTimerRef = useRef(null);
  const HeaderMetaData = useContext(HeaderContext);
  const { clients, dashboardLoading } =
    useStrategyCanvasStoreSelector(selectDashboard);
  const loading = dashboardLoading || clients === null;

  useEffect(() => {
    document.title = "Art of Scale | Strategy Canvas";
    HeaderMetaData.setHeaderMetadata({
      headerTitle: "Strategy Canvas",
    });
    useStrategyCanvasStore.getState().fetchDashboard();
  }, []);

  useEffect(() => {
    return () => clearTimeout(openTimerRef.current);
  }, []);

  const handleToggle = (id) => {
    clearTimeout(openTimerRef.current);

    if (openId === id) {
      setOpenId(null);
      return;
    }

    if (openId === null) {
      setOpenId(id);
      return;
    }

    setOpenId(null);
    openTimerRef.current = setTimeout(() => {
      setOpenId(id);
    }, ACCORDION_TRANSITION_MS);
  };

  return (
    <div>
      <ProjectHeader handleNavigation={() => {}} showBack={false} />
      <div id="strategy-canvas">
        <div className="sc-header">
          <div className="sc-title">Active clients</div>
          <div className="sc-subtitle">
            {loading
              ? ""
              : `${clients.length} ${clients.length === 1 ? "client" : "clients"}`}
          </div>
        </div>
        {loading ? (
          <StrategyCanvasSkeleton count={4} />
        ) : clients.length === 0 ? (
          <div className="sc-empty-state">
            No clients with Strategy Canvas access yet.
          </div>
        ) : (
          <div className="sc-list sc-fade-in">
            {clients.map((sme) => (
              <SmeCanvasCard
                key={sme.id}
                {...sme}
                isOpen={openId === sme.id}
                onToggle={() => handleToggle(sme.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StrategyCanvas;
